import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

export interface CardDataTableAction {
  icon: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
}

export interface CardDataTableColumn {
  id: string;
  header: string;
  align?: 'left' | 'center' | 'right';
  headerClassName?: string;
}

export interface CardDataTableRowDef {
  id: string;
  avatar?: React.ReactNode;
  cells: Record<string, React.ReactNode>;
  expandContent?: React.ReactNode;
  actions?: CardDataTableAction[];
  onClick?: () => void;
}

export interface CardDataTableProps {
  /** CSS grid-template-columns for data columns only (avatar and actions columns are added automatically). */
  gridTemplateColumns: string;
  /** Width of the leading avatar slot. Default: '40px'. */
  avatarWidth?: string;
  /** Width of the trailing actions slot. Default: '52px'. */
  actionsWidth?: string;
  columns: CardDataTableColumn[];
  rows: CardDataTableRowDef[];
  isLoading?: boolean;
  loadingRows?: number;
  errorContent?: React.ReactNode;
  emptyContent?: React.ReactNode;
  className?: string;
}

function SkeletonRow() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white px-5 py-4">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 shrink-0 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-3.5 w-36" />
          <Skeleton className="h-2 w-28" />
        </div>
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-2 w-28 rounded-full" />
      </div>
    </div>
  );
}

export function CardDataTable({
  gridTemplateColumns,
  avatarWidth = '40px',
  actionsWidth = '52px',
  columns,
  rows,
  isLoading,
  loadingRows = 5,
  errorContent,
  emptyContent,
  className,
}: CardDataTableProps) {
  const fullGrid = `${avatarWidth} ${gridTemplateColumns} ${actionsWidth}`;
  const gridStyle: React.CSSProperties = { gridTemplateColumns: fullGrid };

  return (
    <div className={className}>
      {/* Column headers — only shown when data is present */}
      {!isLoading && rows.length > 0 && (
        <div className="mb-1 grid items-center gap-4 px-5 py-2" style={gridStyle}>
          <div />
          {columns.map((col) => (
            <div
              key={col.id}
              className={cn(
                'text-[10px] font-semibold uppercase tracking-wider text-gray-400',
                col.align === 'center' && 'text-center',
                col.align === 'right' && 'text-right',
                col.headerClassName,
              )}
            >
              {col.header}
            </div>
          ))}
          <div />
        </div>
      )}

      <div className="space-y-2.5">
        {isLoading
          ? Array.from({ length: loadingRows }).map((_, i) => <SkeletonRow key={i} />)
          : errorContent && rows.length === 0
            ? errorContent
            : rows.length === 0
              ? emptyContent
              : rows.map((row) => (
                  <CardRow key={row.id} row={row} columns={columns} gridStyle={gridStyle} />
                ))}
      </div>
    </div>
  );
}

function CardRow({
  row,
  columns,
  gridStyle,
}: {
  row: CardDataTableRowDef;
  columns: CardDataTableColumn[];
  gridStyle: React.CSSProperties;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={row.onClick}
      className={cn(
        'group rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200',
        row.onClick && 'cursor-pointer hover:border-brand-200 hover:shadow-lg',
      )}
    >
      <div className="grid items-center gap-4 px-5 py-4" style={gridStyle}>
        <div className="shrink-0">{row.avatar}</div>

        {columns.map((col) => (
          <div
            key={col.id}
            className={cn(
              'min-w-0',
              col.align === 'center' && 'flex items-center justify-center',
              col.align === 'right' && 'flex items-center justify-end',
            )}
          >
            {row.cells[col.id]}
          </div>
        ))}

        <div className="flex shrink-0 items-center justify-end gap-1">
          {row.actions && row.actions.length > 0 && (
            <div
              className={cn(
                'flex items-center gap-0.5 transition-opacity duration-150',
                hovered ? 'opacity-100' : 'opacity-0',
              )}
            >
              {row.actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  title={action.label}
                  onClick={action.onClick}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                >
                  {action.icon}
                </button>
              ))}
            </div>
          )}
          {row.onClick && (
            <ChevronRight
              size={15}
              className={cn(
                'shrink-0 text-gray-400 transition-all duration-150',
                hovered && 'translate-x-0.5 text-brand-500',
              )}
            />
          )}
        </div>
      </div>

      {row.expandContent && (
        <div
          className={cn(
            'overflow-hidden px-5 transition-all duration-200',
            hovered ? 'max-h-12 pb-3 opacity-100' : 'max-h-0 opacity-0',
          )}
        >
          {row.expandContent}
        </div>
      )}
    </div>
  );
}
